import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import CloseBtn from '@components/dataEntry/button/CloseBtn/CloseBtn';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import {
  musicStartYState,
  musicURLState,
  muteState,
  playerOpenState,
  playState,
  pageBgmState,
  globalBgmPausedState,
} from '@atom/setting/jukebox';
import DraggableWrapper from '@components/wrapper/DraggableWrapper';
import { DragContainer } from '@components/wrapper/DragContainer';
import { CollectionPath, SettingCollectionPath } from '@typings/collections.enum';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import useScrollY from '@hooks/useScrollY';
import { jukeboxActiveState } from '@atom/setting/navigation';
import { Ijukebox, IJukeboxItem } from '@typings/setting/jukebox.interface';
import AllPlayList from './MainhomeMusicPlayer/AllPlayList';
import MultiPlayer from './components/MultiPlayer';
import CustomPlaylistPlayer from './components/CustomPlaylistPlayer';
import PlaylistSlidePanel from './components/PlaylistSlidePanel';
import IconButton from '@components/dataEntry/button/IconButton';
import PageBgmPlayer from './components/PageBgmPlayer';

const IconSec = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;

  &:hover {
    cursor: move;
  }

  @media (max-width: 480px) {
    &:hover {
      cursor: default;
    }
  }
`;




type Props = {
  youtubeURL?: string;
  zIndex?: number;
  onActivate?: () => void;
};

function MusicPlayerDraggable({ zIndex, onActivate }: Props) {
  const [open, setOpen] = useRecoilState(playerOpenState);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [slidePanelOpen, setSlidePanelOpen] = useState(false);

  // 슬라이드 패널과 공유하는 플레이어 상태
  const [playerProgress, setPlayerProgress] = useState(0);
  const [playerCurrentTime, setPlayerCurrentTime] = useState(0);
  const [playerDuration, setPlayerDuration] = useState(0);
  const [seekRequestTime, setSeekRequestTime] = useState<number | undefined>(undefined);

  const handlePlayerStateChange = useCallback((progress: number, currentTime: number, duration: number) => {
    setPlayerProgress(progress);
    setPlayerCurrentTime(currentTime);
    setPlayerDuration(duration);
  }, []);

  // 🎵 페이지별 BGM 상태
  const pageBgm = useRecoilValue(pageBgmState);
  const isPageBgmActive = pageBgm.isActive && !!pageBgm.url;

  const close = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
  }, []);

  const { data: jukeboxSetting } = useDocument<Ijukebox>(
    SettingCollectionPath.JUKEBOX,
    { listen: true },
  );
  const { data: playlist } = useCollection<IJukeboxItem>(
    CollectionPath.JUKEBOX,
    { listen: true, orderBy: ['order', 'asc'] },
  );

  // 커스텀 플레이리스트 사용 여부
  const useCustomPlaylist = jukeboxSetting?.useCustomPlaylist && playlist && playlist.length > 0;

  // 슬라이드 패널 토글 (커스텀 플레이리스트 모드일 때만, 페이지 BGM이 아닐 때만)
  const handleListToggle = useCallback(() => {
    if (useCustomPlaylist && !isPageBgmActive) {
      setSlidePanelOpen(!slidePanelOpen);
    }
  }, [useCustomPlaylist, slidePanelOpen, isPageBgmActive]);

  return (
    <>
      <DraggableWrapper direction="bottomRight" onActivate={onActivate}>
        <DragContainer
          open={open}
          floatingDir="bottom"
          width={384}
          onMouseDownCapture={onActivate}
          onTouchStartCapture={onActivate}
          style={{
            backgroundColor: jukeboxSetting?.themeSet?.backgroundColor,
            zIndex,
          }}
        >
          <IconSec style={{ color: jukeboxSetting?.themeSet?.textColor }}>
            {/* 커스텀 플레이리스트 모드일 때만 리스트 버튼 활성화 (페이지 BGM이 아닐 때만) */}
            <IconButton
              icon={'List14'}
              onClick={handleListToggle}
              iconColor={jukeboxSetting?.themeSet?.textColor}
              shape="default"
              size="sm"
              disabled={!useCustomPlaylist || isPageBgmActive}
              tooltip={
                isPageBgmActive
                  ? '페이지 BGM 재생 중'
                  : useCustomPlaylist
                    ? '재생 목록'
                    : '커스텀 플레이리스트를 활성화하세요'
              }
            />
            <CloseBtn
              onClick={close}
              color={jukeboxSetting?.themeSet?.textColor}
            />
          </IconSec>

          {/* 🎵 페이지 BGM 우선 재생 → 전역 BGM(커스텀 플레이리스트 or 단일 URL) */}
          {isPageBgmActive ? (
            <PageBgmPlayer
              url={pageBgm.url!}
              startY={pageBgm.startY}
              jukebox={jukeboxSetting as Ijukebox}
            />
          ) : useCustomPlaylist ? (
            <CustomPlaylistPlayer
              jukebox={jukeboxSetting as Ijukebox}
              playlist={playlist || []}
              currentIndex={currentIndex}
              setCurrentIndex={setCurrentIndex}
              onPlayerStateChange={handlePlayerStateChange}
              seekRequestTime={seekRequestTime}
            />
          ) : (
            <MultiPlayer jukebox={jukeboxSetting as Ijukebox} index={currentIndex} />
          )}
        </DragContainer>
      </DraggableWrapper>

      {/* YouTube Music 스타일 슬라이드 패널 (페이지 BGM이 아닐 때만) */}
      {useCustomPlaylist && !isPageBgmActive && (
        <PlaylistSlidePanel
          isOpen={slidePanelOpen}
          onClose={() => setSlidePanelOpen(false)}
          playlist={playlist || []}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          jukeboxData={jukeboxSetting}
          progress={playerProgress}
          currentTime={playerCurrentTime}
          duration={playerDuration}
          onSeekRequest={setSeekRequestTime}
        />
      )}
    </>
  );
}

export default MusicPlayerDraggable;
